import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import { Button } from './ui/button';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import axios from 'axios';
import { Bell, BellRing, Check, ExternalLink } from 'lucide-react';

const getTypeIcon = (type) => {
  const icons = {
    new_bid: '💼', bid_accepted: '✅', bid_rejected: '❌',
    new_job: '🔍', job_completed: '🏆', new_message: '💬',
    new_review: '⭐', subscription_expiring: '⏳', subscription_expired: '🔴',
    renewal_retry: '🔄', subscription_payment: '💳', verification_submitted: '📄',
    admin_verification_approved: '✅', admin_verification_changes_requested: '📝',
    admin_verification_rejected: '❌', verification_approved: '✅', verification_rejected: '❌',
    system: 'ℹ️'
  };
  return icons[type] || '🔔';
};

const Notifications = () => {
  const navigate = useNavigate();
  const { API, language, activeRole } = useContext(AuthContext);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [open, setOpen] = useState(false);

  const fetchNotifications = useCallback(async () => {
    try {
      const params = activeRole ? `?role=${activeRole}&limit=10` : '?limit=10';
      const countParams = activeRole ? `?role=${activeRole}` : '';
      const [response, countResponse] = await Promise.all([
        axios.get(`${API}/notifications${params}`),
        axios.get(`${API}/notifications/unread-count${countParams}`)
      ]);
      const data = response.data;
      const list = Array.isArray(data) ? data : (data.notifications || []);
      setNotifications(list);
      setUnreadCount(countResponse.data.count || 0);
    } catch (error) {
      // silently ignore
    }
  }, [API, activeRole]);

  // Also poll unread count separately (lightweight)
  const fetchUnreadCount = useCallback(async () => {
    try {
      const params = activeRole ? `?role=${activeRole}` : '';
      const response = await axios.get(`${API}/notifications/unread-count${params}`);
      setUnreadCount(response.data.count || 0);
    } catch {}
  }, [API, activeRole]);

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchUnreadCount, 30000);
    return () => clearInterval(interval);
  }, [fetchNotifications, fetchUnreadCount]);

  // Refresh when dropdown opens
  useEffect(() => {
    if (open) fetchNotifications();
  }, [open, fetchNotifications]);

  const markAsRead = async (notificationId) => {
    try {
      await axios.put(`${API}/notifications/${notificationId}/read`, {});
      setNotifications(prev => prev.map(n => n.id === notificationId ? { ...n, read: true } : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch {}
  };

  const markAllAsRead = async () => {
    try {
      const params = activeRole ? `?role=${activeRole}` : '';
      await axios.put(`${API}/notifications/mark-all-read${params}`, {});
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch {}
  };

  const handleNotifClick = (notif) => {
    if (!notif.read) markAsRead(notif.id);
    if (notif.link) navigate(notif.link);
  };

  const getTimeAgo = (timestamp) => {
    const diffMs = Date.now() - new Date(timestamp).getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);
    const lang = language || 'en';
    if (diffMins < 1) return lang === 'de' ? 'Gerade eben' : lang === 'it' ? 'Adesso' : lang === 'fr' ? 'À l\'instant' : 'Just now';
    if (diffMins < 60) return `${diffMins}${lang === 'de' ? ' Min' : lang === 'it' ? ' min fa' : lang === 'fr' ? ' min' : 'm ago'}`;
    if (diffHours < 24) return `${diffHours}${lang === 'de' ? ' Std' : lang === 'it' ? ' ore fa' : lang === 'fr' ? 'h' : 'h ago'}`;
    return `${diffDays}${lang === 'de' ? ' Tage' : lang === 'it' ? ' giorni fa' : lang === 'fr' ? 'j' : 'd ago'}`;
  };

  const label = {
    en: { title: 'Notifications', markAll: 'Mark all read', viewAll: 'View all notifications', empty: 'No notifications' },
    de: { title: 'Benachrichtigungen', markAll: 'Alle gelesen', viewAll: 'Alle anzeigen', empty: 'Keine Benachrichtigungen' },
    it: { title: 'Notifiche', markAll: 'Segna tutte', viewAll: 'Vedi tutte', empty: 'Nessuna notifica' },
    fr: { title: 'Notifications', markAll: 'Tout marquer lu', viewAll: 'Voir toutes', empty: 'Aucune notification' }
  }[language] || { title: 'Notifications', markAll: 'Mark all read', viewAll: 'View all notifications', empty: 'No notifications' };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative w-9 h-9 rounded-full hover:bg-red-50 transition-colors"
          data-testid="notifications-btn"
          aria-label={unreadCount > 0 ? `Notifications, ${unreadCount} unread` : 'Notifications'}
        >
          {unreadCount > 0 ? (
            <BellRing className="w-5 h-5 text-red-600" strokeWidth={2.5} />
          ) : (
            <Bell className="w-5 h-5 text-gray-600" strokeWidth={2} />
          )}
          {unreadCount > 0 && (
            <span className="absolute -top-0.5 -right-0.5 bg-red-600 text-white text-[9px] min-w-[16px] h-[16px] flex items-center justify-center rounded-full border border-white font-bold leading-none" data-testid="notifications-unread-count">
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-80 p-0 bg-white shadow-xl border border-gray-100"
        align="end"
        sideOffset={8}
        data-testid="notifications-popover"
        style={{ backgroundColor: '#ffffff', opacity: 1, animation: 'none' }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b bg-gray-50 rounded-t-lg">
          <h3 className="font-semibold text-gray-900 text-sm">{label.title}</h3>
          {unreadCount > 0 && (
            <button
              onClick={markAllAsRead}
              className="text-xs text-red-600 hover:text-red-700 font-medium flex items-center gap-1"
              data-testid="mark-all-read-btn"
            >
              <Check className="w-3 h-3" />
              {label.markAll}
            </button>
          )}
        </div>

        {/* List */}
        <div className="max-h-[360px] overflow-y-auto bg-white" style={{ backgroundColor: '#ffffff' }}>
          {notifications.length === 0 ? (
            <div className="py-10 text-center text-gray-400 text-sm">
              <Bell className="w-8 h-8 mx-auto mb-2 opacity-30" />
              {label.empty}
            </div>
          ) : (
            notifications.map((notif) => (
              <div
                key={notif.id}
                className={`px-4 py-3 border-b last:border-b-0 cursor-pointer transition-colors hover:bg-gray-50 ${
                  !notif.read ? 'bg-red-50' : 'bg-white'
                }`}
                style={{ backgroundColor: !notif.read ? '#fef2f2' : '#ffffff' }}
                onClick={() => handleNotifClick(notif)}
                data-testid={`notification-item-${notif.id}`}
              >
                <div className="flex gap-3 items-start">
                  <span className="text-base mt-0.5 flex-shrink-0" data-testid={`notification-icon-${notif.id}`}>{getTypeIcon(notif.type)}</span>
                  <div className="flex-1 min-w-0">
                    {notif.title && (
                      <p className="text-xs font-semibold text-gray-900 leading-tight mb-0.5 truncate" data-testid={`notification-title-${notif.id}`}>{notif.title}</p>
                    )}
                    <p className="text-xs text-gray-600 leading-relaxed line-clamp-2" data-testid={`notification-message-${notif.id}`}>{notif.message}</p>
                    <p className="text-[10px] text-gray-400 mt-1" data-testid={`notification-time-${notif.id}`}>{getTimeAgo(notif.created_at)}</p>
                  </div>
                  {!notif.read && (
                    <div className="w-2 h-2 bg-red-500 rounded-full flex-shrink-0 mt-1.5" />
                  )}
                </div>
              </div>
            ))
          )}
        </div>

        {/* Footer */}
        <div className="border-t px-4 py-2.5 bg-gray-50 rounded-b-lg">
          <button
            onClick={() => { setOpen(false); navigate('/notifications'); }}
            className="w-full text-xs text-red-600 hover:text-red-700 font-medium flex items-center justify-center gap-1.5 py-1"
            data-testid="view-all-notifications-btn"
          >
            <ExternalLink className="w-3.5 h-3.5" />
            {label.viewAll}
          </button>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default Notifications;
